import { apiClient } from './api'

export interface CrimeCategoryCacheQuery {
  page: number
  pageSize: number
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  search?: string
  crimeCategory?: string
  source?: string
  isGroundTruth?: boolean
  confidenceMin?: number
  confidenceMax?: number
  methodDisagreement?: boolean
}

export interface CrimeCategoryCacheRow {
  id: string
  charge_normalized: string
  crime_category: string
  confidence: number | null
  crime_category_knn: string | null
  crime_category_regex: string | null
  crime_category_llm: string | null
  method_scores: string | null
  is_ground_truth: boolean | null
  source: string | null
  sample_count: number | null
  version: number
  created_at: string | null
  updated_at: string | null
}

export interface CrimeCategoryStats {
  totalRows: number
  lowConfidenceCount: number
  manualOverrideCount: number
  groundTruthCount: number
  byCategoryBreakdown: {
    Criminal: number
    Traffic: number
    Other: number
  }
}

export interface CrimeCategoryCacheResponse {
  rows: CrimeCategoryCacheRow[]
  total: number
  page: number
  pageSize: number
  stats: CrimeCategoryStats
}

interface ApiResponse<T> {
  success: boolean
  data: T
}

export interface BatchUpdateItem {
  id: string
  crime_category: string
  version: number
}

export interface BatchUpdateResponse {
  successIds: string[]
  conflictIds: string[]
  errorIds: string[]
  totalUpdated: number
  totalConflicts: number
  totalErrors: number
}

export const getCrimeCategoryCache = async (query: CrimeCategoryCacheQuery) => {
  const params: Record<string, any> = {
    page: query.page,
    pageSize: query.pageSize,
  }
  if (query.sortBy) params.sortBy = query.sortBy
  if (query.sortOrder) params.sortOrder = query.sortOrder
  if (query.search) params.search = query.search
  if (query.crimeCategory) params.crimeCategory = query.crimeCategory
  if (query.source) params.source = query.source
  if (query.isGroundTruth !== undefined) params.isGroundTruth = String(query.isGroundTruth)
  if (query.confidenceMin !== undefined) params.confidenceMin = query.confidenceMin
  if (query.confidenceMax !== undefined) params.confidenceMax = query.confidenceMax
  if (query.methodDisagreement) params.methodDisagreement = 'true'

  const response = await apiClient.get<ApiResponse<CrimeCategoryCacheResponse>>(
    '/labeling/crime-category',
    params,
  )
  return response.data
}

export const batchUpdateCrimeCategory = async (updates: BatchUpdateItem[]) => {
  const response = await apiClient.put<ApiResponse<BatchUpdateResponse>>(
    '/labeling/crime-category/batch',
    { updates },
  )
  return response.data
}

// ---------------------------------------------------------------------------
// Label queues — charge_labels and source_labels (5-class)
// Attempted Murder was merged into Murder. Live v1 may still predict it.
// ---------------------------------------------------------------------------

export const CHARGE_CATEGORIES = [
  'Traffic', 'Drug Offense', 'Fraud', 'Theft', 'Assault', 'Weapons Offense',
  'DUI', 'Sex Offense', 'Court Violation', 'Obstruction', 'Immigration Offense',
  'Alcohol Offense', 'Property Damage', 'Disorderly Conduct', 'Trespassing',
  'Aiding/Abetting', 'Pet Offense', 'Fleeing/Eluding', 'Child Abuse',
  'Harassment', 'Murder', 'Violation of Protection Order', 'Hunting/Wildlife',
  'Kidnapping',
] as const

// Extra categories requested by reviewers that the model was NOT trained on.
// Captured as data only; shown as a separate group in the labeling dropdown.
export const EXTRA_CHARGE_CATEGORIES = [
  'Animal Abuse', 'Business Offense', 'Bribery/Extortion', 'Court Event',
  'Customs', 'Fugitive', 'Gambling',
  'Property Owner Offense', 'Incarceration Offense', 'Racketeering and Conspiracy', 'Truancy',
] as const

export const SOURCE_CATEGORIES = [
  'Courts', 'Department of Corrections', 'Registries and International',
  'Offender Registries', 'Warrant and Wanted',
] as const

export type LabelStatus = 'PENDING' | 'LABELED' | 'CONFIRMED' | 'REJECTED' | 'RELABELED'

export interface LabelQueueStats {
  totalRows: number
  pendingCount: number
  labeledCount: number
  confirmedCount: number
  rejectedCount: number
  relabeledCount: number
  groundTruthCount: number
  lowConfidenceCount?: number
  cohortRemainingCount?: number
  cohortLabeledCount?: number
}

export interface LabelBatchResponse {
  successIds: string[]
  errorIds: string[]
  totalUpdated: number
  totalErrors: number
}

// ---- Charge labels --------------------------------------------------------

export interface ChargeLabelQuery {
  page: number
  pageSize: number
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  search?: string
  predictedCategory?: string
  humanLabel?: string
  labelStatus?: string
  cohort?: string
  isGroundTruth?: boolean
  confidenceMin?: number
  confidenceMax?: number
}

export interface ChargeLabelRow {
  charge_hash: string
  charge_clean: string
  statute_clean: string | null
  state_clean: string | null
  predicted_category: string | null
  predicted_confidence: number | null
  predicted_model_version: string | null
  human_label: string | null
  label_status: LabelStatus
  is_ground_truth: boolean | null
  labeled_by: string | null
  frequency: number | null
  labeled_at: string | null
  created_at: string | null
  cohort: string | null
}

export interface ChargeLabelResponse {
  rows: ChargeLabelRow[]
  total: number
  page: number
  pageSize: number
  stats: LabelQueueStats
}

export interface ChargeLabelUpdateItem {
  charge_hash: string
  human_label?: string | null
  label_status?: LabelStatus
  is_ground_truth?: boolean
}

export const getChargeLabels = async (query: ChargeLabelQuery) => {
  const params: Record<string, any> = { page: query.page, pageSize: query.pageSize }
  if (query.sortBy) params.sortBy = query.sortBy
  if (query.sortOrder) params.sortOrder = query.sortOrder
  if (query.search) params.search = query.search
  if (query.predictedCategory) params.predictedCategory = query.predictedCategory
  if (query.humanLabel) params.humanLabel = query.humanLabel
  if (query.labelStatus) params.labelStatus = query.labelStatus
  if (query.cohort) params.cohort = query.cohort
  if (query.isGroundTruth !== undefined) params.isGroundTruth = String(query.isGroundTruth)
  if (query.confidenceMin !== undefined) params.confidenceMin = query.confidenceMin
  if (query.confidenceMax !== undefined) params.confidenceMax = query.confidenceMax

  const response = await apiClient.get<ApiResponse<ChargeLabelResponse>>('/labeling/charges', params)
  return response.data
}

export const batchUpdateChargeLabels = async (updates: ChargeLabelUpdateItem[]) => {
  const response = await apiClient.put<ApiResponse<LabelBatchResponse>>(
    '/labeling/charges/batch',
    { updates },
  )
  return response.data
}

// ---- Source labels --------------------------------------------------------

export interface SourceLabelQuery {
  page: number
  pageSize: number
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  search?: string
  humanLabel?: string
  labelStatus?: string
  isGroundTruth?: boolean
}

export interface SourceLabelRow {
  id: string
  source_raw: string
  human_label: string | null
  label_status: LabelStatus
  is_ground_truth: boolean | null
  labeled_by: string | null
  labeled_at: string | null
  created_at: string | null
}

export interface SourceLabelResponse {
  rows: SourceLabelRow[]
  total: number
  page: number
  pageSize: number
  stats: LabelQueueStats
}

export interface SourceLabelUpdateItem {
  id: string
  human_label?: string | null
  label_status?: LabelStatus
  is_ground_truth?: boolean
}

export const getSourceLabels = async (query: SourceLabelQuery) => {
  const params: Record<string, any> = { page: query.page, pageSize: query.pageSize }
  if (query.sortBy) params.sortBy = query.sortBy
  if (query.sortOrder) params.sortOrder = query.sortOrder
  if (query.search) params.search = query.search
  if (query.humanLabel) params.humanLabel = query.humanLabel
  if (query.labelStatus) params.labelStatus = query.labelStatus
  if (query.isGroundTruth !== undefined) params.isGroundTruth = String(query.isGroundTruth)

  const response = await apiClient.get<ApiResponse<SourceLabelResponse>>('/labeling/sources', params)
  return response.data
}

export const batchUpdateSourceLabels = async (updates: SourceLabelUpdateItem[]) => {
  const response = await apiClient.put<ApiResponse<LabelBatchResponse>>(
    '/labeling/sources/batch',
    { updates },
  )
  return response.data
}
